import { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  buildDefaultTreasuryConfig,
  normalizeTreasuryConfig,
  buildTreasuryEntityPrefixes,
} from '../../shared/utils/cashTreasuryHelpers';

let hydratePromise = null;

function isTreasuryConfigHydrated(treasury) {
  return treasury?._syncedFromApi === true;
}

function hydrateTreasuryConfig(patchSettings) {
  if (!hydratePromise) {
    hydratePromise = localApiAdapter
      .get('/treasury/config')
      .then((data) => {
        const normalized = normalizeTreasuryConfig({ ...buildDefaultTreasuryConfig(), ...(data || {}) });
        syncTreasuryConfigToSettings(normalized, patchSettings);
        return normalized;
      })
      .catch(() => {
        hydratePromise = null;
        return buildDefaultTreasuryConfig();
      });
  }
  return hydratePromise;
}

/** Live treasury module config — synced from API once, then from SettingsContext. */
export function useTreasuryConfig() {
  const { settings, patchSettings } = useSettings();

  useEffect(() => {
    if (isTreasuryConfigHydrated(settings?.treasury)) return;
    void hydrateTreasuryConfig(patchSettings);
  }, [settings?.treasury, patchSettings]);

  return useMemo(
    () => normalizeTreasuryConfig(settings?.treasury || {}),
    [settings?.treasury],
  );
}

export function syncTreasuryConfigToSettings(config, patchSettings, existingSettings = {}) {
  const normalized = normalizeTreasuryConfig(config);
  patchSettings({
    treasury: { ...normalized, _syncedFromApi: true },
    configuration: {
      entityPrefixes: {
        ...(existingSettings?.configuration?.entityPrefixes || {}),
        ...buildTreasuryEntityPrefixes(normalized),
      },
    },
  }, { persist: false });
  return normalized;
}

/** Sync table/cards toggle with treasury config defaultView (real-time). */
export function useTreasuryListViewMode(treasuryConfig) {
  const [viewMode, setViewMode] = useState(() => treasuryConfig?.defaultView || 'table');

  useEffect(() => {
    const mapped = treasuryConfig?.defaultView === 'journal' || treasuryConfig?.defaultView === 'main'
      ? 'table'
      : (treasuryConfig?.defaultView || 'table');
    setViewMode(mapped);
  }, [treasuryConfig?.defaultView]);

  return [viewMode, setViewMode];
}
